import { DashboardTableType } from "@/types/enums/DashboardTableType";
import { DashboardTheme } from "@/types/enums/DashboardTheme";
import { DataType } from "@/types/enums/DataType";
import { EmailSentFields } from "@/types/enums/EmailSentFields";
import { EventFields } from "@/types/enums/EventFields";
import { GuestFields } from "@/types/enums/GuestFields";
import { GuestOrigin } from "@/types/enums/GuestOrigin";
import { Field } from "@/types/Field";
import { TableFilter } from "@/types/TableFilter";
import { TableView } from "@/types/TableView";
import { LOG_SOURCE_OPTIONS } from "@/types/WorkflowExecution";
import { Plugin } from "@/types/enums/Plugin";
import { locales } from "@/i18n/config";
import { EMAIL_RULE_MAX_LENGTH, EMAIL_RULE_PATTERN_SOURCE } from "@/lib/email-rule";
import { TIMEZONE_OPTIONS } from "@/lib/utils/timezoneOptions";

export { TIMEZONE_OPTIONS };

export const ALL_LANGUAGE_OPTIONS = [
  "af-ZA", "am-ET", "ar-SA", "az-AZ", "be-BY", "bg-BG", "bn-BD", "bs-BA",
  "ca-AD", "ca-ES", "cs-CZ", "cy-GB", "da-DK", "de-AT", "de-BE", "de-CH",
  "de-DE", "de-LI", "el-GR", "en-CA", "en-GB", "en-IE", "en-US", "es-AR",
  "es-CL",
  "es-CO", "es-ES", "es-MX", "es-PE", "es-US", "et-EE", "eu-ES", "fa-IR",
  "fi-FI", "fr-BE", "fr-CA", "fr-CH", "fr-FR", "fr-MC", "ga-IE", "gd-GB",
  "gl-ES", "gu-IN", "ha-NG", "he-IL", "hi-IN", "hr-HR", "hu-HU", "hy-AM",
  "id-ID", "is-IS", "it-CH", "it-IT", "it-SM", "it-VA", "ja-JP", "ka-GE",
  "kk-KZ", "km-KH", "kn-IN", "ko-KR", "ky-KG", "lb-LU", "lo-LA", "lt-LT",
  "lv-LV", "mk-MK", "ml-IN", "mn-MN", "mr-IN", "ms-MY", "mt-MT", "my-MM",
  "nb-NO", "ne-NP", "nl-BE", "nl-NL", "pa-IN", "pl-PL", "ps-AF", "pt-BR",
  "pt-PT", "rm-CH", "ro-RO", "ru-RU", "si-LK", "sk-SK", "sl-SI", "so-SO",
  "sq-AL", "sr-RS", "sv-SE", "sw-KE", "ta-IN", "te-IN", "tg-TJ", "th-TH",
  "tk-TM", "tl-PH", "tr-TR", "uk-UA", "ur-PK", "uz-UZ", "vi-VN", "yo-NG",
  "zh-CN", "zh-TW", "zu-ZA",
];

export const DEFAULT_DASHBOARD_VIEW = [
  // Row 0-1: Event Hero Banner with embedded quick-nav links
  {
    dashboardCardId: "667a1b2c3d4e5f6789abc001",
    name: "eventHero",
    width: 4,
    height: 2,
    x: 0,
    y: 0,
    theme: DashboardTheme.EVENT_HERO,
    tableType: DashboardTableType.CUSTOM,
    fields: [
      "name",
      "start",
      "end",
      "timeZone",
      "locationName",
      "address",
      "city",
      "country",
      "guestlist",
      "requests",
      "checkin",
      "email",
    ],
  },
  // Row 2-3: Guest Overview (2 cols) + Recent Activity (2 cols)
  {
    dashboardCardId: "667a1b2c3d4e5f6789abc002",
    name: "guestOverview",
    width: 2,
    height: 2,
    x: 0,
    y: 2,
    preSelector: "state",
    theme: DashboardTheme.GUEST,
    tableType: DashboardTableType.PIE,
    fields: [],
  },
  {
    dashboardCardId: "667a1b2c3d4e5f6789abc009",
    name: "recentActivity",
    width: 2,
    height: 2,
    x: 2,
    y: 2,
    preSelector: "guests",
    theme: DashboardTheme.ACTIVITY,
    tableType: DashboardTableType.CUSTOM,
    fields: [],
  },
  // Row 4-5: Email Activity + Request Feed side by side
  {
    dashboardCardId: "667a1b2c3d4e5f6789abc011",
    name: "emailActivity",
    width: 2,
    height: 2,
    x: 0,
    y: 4,
    preSelector: "emails",
    theme: DashboardTheme.ACTIVITY,
    tableType: DashboardTableType.CUSTOM,
    fields: [],
  },
  {
    dashboardCardId: "667a1b2c3d4e5f6789abc010",
    name: "pendingRequests",
    width: 2,
    height: 2,
    x: 2,
    y: 4,
    preSelector: "requests",
    theme: DashboardTheme.ACTIVITY,
    tableType: DashboardTableType.CUSTOM,
    fields: [],
  },
];

export const EVENT_FIELDS_DASHBOARD = [
  {
    dataType: DataType.STRING,
    name: "name",
    additionalField: false,
    editable: true,
  },
  {
    dataType: DataType.SINGLE_SELECT,
    name: "registrationType",
    additionalField: false,
    options: ["INVITATION", "LOCKED", "OPEN"],
    required: true,
    editable: true,
  },
  {
    dataType: DataType.STRING,
    name: "locationName",
    additionalField: false,
    editable: true,
  },
  {
    dataType: DataType.STRING,
    name: "address",
    additionalField: false,
    editable: true,
  },
  {
    dataType: DataType.STRING,
    name: "address2",
    additionalField: false,
    editable: true,
  },
  {
    dataType: DataType.STRING,
    name: "zipCode",
    additionalField: false,
    editable: true,
  },
  {
    dataType: DataType.STRING,
    name: "city",
    additionalField: false,
    editable: true,
  },
  {
    dataType: DataType.STRING,
    name: "country",
    additionalField: false,
    editable: true,
  },
  {
    dataType: DataType.DATE_TIME,
    name: "start",
    additionalField: false,
    editable: true,
  },
  {
    dataType: DataType.DATE_TIME,
    name: "end",
    additionalField: false,
    editable: true,
  },
  {
    dataType: DataType.SINGLE_SELECT,
    name: "timeZone",
    additionalField: false,
    options: TIMEZONE_OPTIONS,
    required: true,
    editable: true,
  },
];

// Default fields configuration
const DEFAULT_EVENT_FIELDS = [
  EventFields.id,
  EventFields.cta,
  EventFields.name,
  EventFields.locationName,
  EventFields.registrationType,
  EventFields.start,
  EventFields.end,
  EventFields.createdAt,
];

const DEFAULT_EMAIL_SENT_FIELDS = [
  EmailSentFields.emailStatus,
  EmailSentFields.recipientMail,
  EmailSentFields.templateName,
  EmailSentFields.sendName,
  EmailSentFields.sentAt,
  EmailSentFields.firstName,
  EmailSentFields.lastName,
];

const DEFAULT_GUEST_FIELDS = [
  // `id` is the short, readable identifier. The 24-char `guestId` ObjectId is
  // deliberately NOT here: it carries nothing a reader can use and only costs
  // column width. It stays available through the column picker, and the row
  // still receives it either way — the guest projection is a fixed field list
  // in GuestHandling, independent of the view (structural identifiers are also
  // exempt from a CUSTOM role's allowlist via ALLOWLIST_EXEMPT_FIELDS).
  GuestFields.id,
  // W3-G: restore the check-in status/CTA columns that were dropped from the
  // default guest projection (the "Check-In-Status" filter went missing on the
  // guest list).
  GuestFields.state,
  GuestFields.gender,
  GuestFields.firstName,
  GuestFields.lastName,
  GuestFields.email,
  GuestFields.mobile,
  GuestFields.checkedIn,
  GuestFields.checkInOutCTA,
  GuestFields.createdAt,
];

const DEFAULT_COMPANIONS_FIELDS = [
  GuestFields.companions,
  GuestFields.id,
  GuestFields.firstName,
  GuestFields.lastName,
  GuestFields.email,
  GuestFields.actionCompanion,
];

const DEFAULT_REGISTER_FIELDS = [
  GuestFields.id,
  GuestFields.firstName,
  GuestFields.lastName,
  GuestFields.email,
  GuestFields.mobile,
  GuestFields.actionRegistrant,
];

const DEFAULT_CHECK_IN_FIELDS = [
  "id",
  "checkInOutStatus",
  "guestFirstName",
  "guestLastName",
  "guestEmail",
  "action",
  "createdAt",
  "performedByType",
  "deviceName",
  "checkInLogCTA",
];

// Wave A — speaker/sponsor hub DataTable defaults. `sortIndex` leads: its
// cell hosts the up/down reorder controls while the view is pristine (default
// sort, no search/filter) — see SpeakersRows/SponsorsRows.
const DEFAULT_SPEAKER_FIELDS = [
  "sortIndex",
  "imageUrl",
  "name",
  "occupation",
  "company",
  "email",
  "socials",
];

const DEFAULT_SPONSOR_FIELDS = [
  "sortIndex",
  "logoUrl",
  "name",
  "tier",
  "contactName",
  "linkUrl",
];

const DEFAULT_ACTION_LOG_FIELDS = [
  "createdAt",
  "pipeName",
  "trigger",
  "guest",
  "status",
  "durationMs",
];

export type TriggerGroup = {
  category: string;
  triggers: string[];
};

export const triggerGroups: TriggerGroup[] = [
  {
    // Selecting SCHEDULED turns the workflow into a one-shot scheduled
    // broadcast — it is exclusive (handled in PWorkflowEditor).
    category: "triggerCategorySchedule",
    triggers: ["SCHEDULED"],
  },
  {
    // Ordered along the guest's own journey — record, answer, verdict,
    // companion, waitlist, door — and NOT by when each trigger was added.
    // The order matters because the picker prints this array verbatim: the
    // three-member request and companion families used to be split across the
    // list with unrelated rows between them, so an author who found "request
    // submitted" had no reason to believe its two verdicts existed at all.
    // `triggerCatalogue.test.ts` pins the sequence for the same reason.
    category: "triggerCategoryGuests",
    triggers: [
      // The guest record itself.
      "GUEST_CREATED", "GUEST_UPDATED", "GUEST_DELETED",
      // The guest's own answer. Derived by the backend from the `state` diff,
      // so they fire for every writer that goes through GuestService —
      // cockpit, public website, the attendee portal AND the invitation-email
      // link, which until now fired no automation at all.
      "GUEST_ACCEPTED", "GUEST_DECLINED",
      // Registration requests: the intake, then the two verdicts on it,
      // derived by the backend from the `acceptanceState` diff.
      //
      // Usually the organiser's verdict, but NOT always: the one-click ACCEPT
      // link in the invitation email writes `acceptanceState=ACCEPTED` itself
      // (`GuestActionResource.java:130`) and cascades it to every pending
      // companion, so a guest can fire REQUEST_APPROVED / COMPANION_APPROVED
      // with nobody in the cockpit. DECLINE deliberately does not — it leaves
      // `acceptanceState` alone — so the pair is asymmetric.
      //
      // REQUEST_SUBMITTED is ungated on purpose — see TRIGGER_SOURCE for the
      // three doors it fires from, two of them without any website.
      "REQUEST_SUBMITTED", "REQUEST_APPROVED", "REQUEST_DENIED",
      // Companions: the admin-suite member of the COMPANION_ADDED family
      // (cockpit create doors, the placeholder quick-add, import, CRM, Velo
      // AI — not clone and not speaker promotion, those insert through a lane
      // that fires no create trigger at all, by design), then its two
      // verdicts. Under GUESTS and therefore ungated — this is the one door
      // every event has, with or without the website plugin.
      //
      // Its two siblings sit under Website and Check-in app, where the plugin
      // gate is correct because those doors do not exist without the plugin.
      // The whole family used to be ONE trigger parked here for exactly that
      // reason; splitting it by door is what let each member move to the
      // category that actually describes it.
      "COMPANION_ADDED_ADMIN", "COMPANION_APPROVED", "COMPANION_DENIED",
      // Fired by the automatic waitlist promotion (`GuestService:1937`) and
      // by every state write that frees a seat, but never offered here — so
      // the one door that admits a guest without anyone clicking was the one
      // door no rule could subscribe to. `GUEST_UPDATED` is not a substitute:
      // the derived fan-out explicitly skips past GUEST_PROMOTED.
      "GUEST_PROMOTED",
      // Arrival, last: the end of the journey, and the only pair here that a
      // rule usually subscribes to on its own.
      "GUEST_CHECKIN", "GUEST_CHECKOUT",
    ],
  },
  {
    category: "triggerCategoryWebsite",
    triggers: [
      "GUEST_CREATED_WEBSITE", "GUEST_UPDATED_WEBSITE",
      // Fires ONLY from the two public doors (registration, and a logged-in
      // guest adding one later), so the WEBSITE plugin gate is correct here.
      // Existing rules keep working on an event that later loses the plugin:
      // `categoryAvailable` keeps a gated category visible once the open
      // workflow has used one of its triggers, and keeps it visible for the
      // rest of that editing session even after the operator unchecks it.
      "COMPANION_ADDED",
    ],
  },
  {
    category: "triggerCategoryCheckInApp",
    triggers: [
      // The door's walk-up create — a guest who was never on the list. Fired
      // by main-backend on the device's behalf, additively to GUEST_CREATED.
      "GUEST_CREATED_APP",
      "GUEST_CHECKIN_APP", "GUEST_CHECKOUT_APP", "GUEST_UPDATED_APP",
      "GUEST_APPOINTMENT_CHECKIN_APP", "GUEST_SEATING_CHECKIN_APP",
      // Walk-up "+1" at the door. Fired by main-backend on the device's behalf
      // (InternalCheckinResource), which is why it is absent from the Worker.
      "COMPANION_ADDED_APP",
    ],
  },
  {
    category: "triggerCategoryAppointments",
    triggers: [
      // Same rule as the guests group: the booking's own journey first, the
      // operator-facing alarms last. The request that precedes a booking used
      // to sit BEHIND the two waitlist triggers, four rows away from the
      // booking it turns into.
      "APPOINTMENT_REQUEST_CREATED", "APPOINTMENT_CREATED",
      "APPOINTMENT_CONFIRMED", "APPOINTMENT_CHECKED_IN",
      "APPOINTMENT_CANCELLED",
      "APPOINTMENT_WAITLIST_JOINED", "APPOINTMENT_WAITLIST_PROMOTED",
      // Not events on one booking — they watch the slot and the staff.
      "APPOINTMENT_BELOW_THRESHOLD", "STAFF_CANCELLATION_ALERT",
    ],
  },
  {
    category: "triggerCategorySeating",
    triggers: [
      "SEAT_ASSIGNED", "SEAT_UNASSIGNED", "SEAT_CONFIRMED", "SEAT_RESERVED",
    ],
  },
];

export const emailTriggers = triggerGroups.flatMap(g => g.triggers);

/**
 * Origin channel of each trigger — drives the small source badge in the
 * workflow trigger picker. It disambiguates the near-duplicate labels
 * (three "Guest updated", four check-in variants) that otherwise read as
 * accidental copies, and stays meaningful in search results where the
 * category header is no longer visible. The label per source is i18n'd in
 * the picker (organizer = new key, the rest reuse triggerCategory*).
 */
export type TriggerSource =
  | "ORGANIZER"
  | "WEBSITE"
  | "CHECKIN_APP"
  | "APPOINTMENTS"
  | "SEATING";

export const TRIGGER_SOURCE: Record<string, TriggerSource> = {
  GUEST_CREATED: "ORGANIZER",
  GUEST_UPDATED: "ORGANIZER",
  GUEST_DELETED: "ORGANIZER",
  GUEST_CHECKIN: "ORGANIZER",
  GUEST_CHECKOUT: "ORGANIZER",
  // REQUEST_SUBMITTED carries NO badge, deliberately. It read "WEBSITE" and
  // that is false for two of its three doors: the cockpit's own "add request"
  // button (GuestResource `/create-pending`) and the companion-request
  // endpoint, neither of which touches a website, and both of which work on an
  // event with no website plugin at all. The public form
  // (website-builder-backend `runtime.ts`) is one door among them, not the door.
  //
  // THREE doors, not the six an earlier version of this comment claimed. The
  // trigger fires only for a row that lands PENDING (`GuestService.java:5522`),
  // and the other lanes through that same emit site stamp SYSTEM first: batch
  // create (`GuestResource.java:527` — "DIRECT and REQUEST both land SYSTEM. An
  // admin who wants a real request row uses /create-pending"), the CRM's
  // add-to-event (`ContactService.java:744`) and the CSV import
  // (`HeavyProcessingService.java:1054`). Reaching the emit site is not
  // reaching its condition — and a description naming an import door that
  // cannot fire is the same false promise as the badge, pointing the other way.
  //
  // Absence is what this file already says about a multi-door trigger:
  // GUEST_ACCEPTED, GUEST_DECLINED, the four verdicts and GUEST_PROMOTED are
  // all missing here for the same reason, and the picker renders them with no
  // badge. A badge naming ONE door of several is the exact mistake the
  // COMPANION_ADDED split was built to end — see the note below.
  // One badge per door — the whole point of splitting the family. This badge
  // used to sit on a trigger that fired from every door and was the reason
  // operators believed the cockpit never announced a companion at all.
  COMPANION_ADDED: "WEBSITE",
  COMPANION_ADDED_ADMIN: "ORGANIZER",
  COMPANION_ADDED_APP: "CHECKIN_APP",
  GUEST_CREATED_WEBSITE: "WEBSITE",
  GUEST_UPDATED_WEBSITE: "WEBSITE",
  GUEST_CREATED_APP: "CHECKIN_APP",
  GUEST_CHECKIN_APP: "CHECKIN_APP",
  GUEST_CHECKOUT_APP: "CHECKIN_APP",
  GUEST_UPDATED_APP: "CHECKIN_APP",
  GUEST_APPOINTMENT_CHECKIN_APP: "CHECKIN_APP",
  GUEST_SEATING_CHECKIN_APP: "CHECKIN_APP",
  APPOINTMENT_CREATED: "APPOINTMENTS",
  APPOINTMENT_CONFIRMED: "APPOINTMENTS",
  APPOINTMENT_CANCELLED: "APPOINTMENTS",
  APPOINTMENT_CHECKED_IN: "APPOINTMENTS",
  APPOINTMENT_WAITLIST_JOINED: "APPOINTMENTS",
  APPOINTMENT_WAITLIST_PROMOTED: "APPOINTMENTS",
  APPOINTMENT_REQUEST_CREATED: "APPOINTMENTS",
  APPOINTMENT_BELOW_THRESHOLD: "APPOINTMENTS",
  STAFF_CANCELLATION_ALERT: "APPOINTMENTS",
  SEAT_ASSIGNED: "SEATING",
  SEAT_UNASSIGNED: "SEATING",
  SEAT_CONFIRMED: "SEATING",
  SEAT_RESERVED: "SEATING",
};

/**
 * Capability gate per trigger category — the event plugin that must be
 * enabled for the category to be offered in the picker. `null` = always
 * available (core guest events + the scheduled broadcast). The picker
 * still shows a gated category when the current workflow already uses one
 * of its triggers, so gating never silently drops an in-use trigger — and
 * keeps showing it for the rest of that editing session even after the last
 * one is unchecked, so the operator can undo their own click.
 */
export const TRIGGER_CATEGORY_PLUGIN: Record<string, Plugin | null> = {
  triggerCategorySchedule: null,
  triggerCategoryGuests: null,
  triggerCategoryWebsite: Plugin.WEBSITE,
  triggerCategoryCheckInApp: Plugin.CHECK_IN,
  triggerCategoryAppointments: Plugin.SLOTTING,
  triggerCategorySeating: Plugin.SEATING,
};

export const FIELDS_ACTION_LOG: Field[] = [
  { name: "createdAt", dataType: DataType.DATE_TIME, additionalField: false, editable: false },
  { name: "pipeName", dataType: DataType.STRING, additionalField: false, editable: false },
  {
    name: "trigger",
    dataType: DataType.SINGLE_SELECT,
    additionalField: false,
    editable: false,
    options: emailTriggers,
    // The table cell already resolves these through `triggerLabel` in the
    // `mailTemplateCreation` namespace. The FILTER did not — and
    // `optionsMultiSingleSelect` has no `GUEST_CREATED` etc., so the trigger
    // picker on this page has been showing raw enum keys. Same mechanism as
    // `source` below; fixed together because fixing only one reads arbitrary.
    optionNamespace: "mailTemplateCreation",
  },
  { name: "guest", dataType: DataType.STRING, additionalField: false, editable: false },
  {
    name: "source",
    dataType: DataType.SINGLE_SELECT,
    additionalField: false,
    editable: false,
    // Spread, not the tuple itself: `options` is `string[]` and a readonly
    // tuple is not assignable to it. The single list lives in
    // types/WorkflowExecution.ts — see the note there on why the direction of
    // this import matters.
    options: [...LOG_SOURCE_OPTIONS],
    // `automation` — the same catalogue ActionLogsRows/PLogDetails render the
    // cell with, so the chip and the cell cannot disagree.
    optionNamespace: "automation",
  },
  {
    name: "status",
    dataType: DataType.SINGLE_SELECT,
    additionalField: false,
    editable: false,
    options: ["SUCCESS", "PARTIAL_FAILURE", "FAILURE"],
    // The THIRD select column of this table, and it was left out when `source`
    // and `trigger` got their pointers — while the comment on `trigger` said
    // "fixed together because fixing only one reads arbitrary". Two of three is
    // the same arbitrariness.
    //
    // The disagreement was live: `ActionLogsRows` renders the cell with
    // `t(value)` in `automation` ("Success" / "Partial Failure" / "Failure"),
    // while the filter went through the shared catalogue, which spells SUCCESS
    // differently ("Successful") and has no entry at all for the other two — so
    // the dropdown offered `PARTIAL_FAILURE` and `FAILURE` raw, next to a column
    // reading them properly.
    optionNamespace: "automation",
  },
  { name: "durationMs", dataType: DataType.LONG, additionalField: false, editable: false },
];

export const FIELDS_CHECK_IN_LOG: Field[] = [
  { name: "id", dataType: DataType.ID, additionalField: false, editable: false },
  { name: "guestFirstName", dataType: DataType.STRING, additionalField: false, editable: false },
  { name: "guestLastName", dataType: DataType.STRING, additionalField: false, editable: false },
  { name: "guestEmail", dataType: DataType.EMAIL, additionalField: false, editable: false },
  // TRI-state, like `guests.checkedIn` itself. NOT_CHECKED_IN is the
  // never-scanned bucket — the state a check-in reset restores. It used to be
  // folded into CHECKED_OUT by the backend's two-armed $cond, so the column
  // asserted that a guest had left when they had never arrived.
  { name: "checkInOutStatus", dataType: DataType.SINGLE_SELECT, additionalField: false, editable: false, options: ["CHECKED_IN", "CHECKED_OUT", "NOT_CHECKED_IN"] },
  // CHECK_IN_RESET is filterable for the same reason it is written at all:
  // finding the mis-scans somebody repaired is the question the reset creates.
  { name: "action", dataType: DataType.CHECK_IN_LOG_ACTION, additionalField: false, options: ["CHECK_IN", "CHECK_OUT", "SESSION_CHECK_IN", "CHECK_IN_RESET"], editable: false },
  { name: "createdAt", dataType: DataType.DATE_TIME, additionalField: false, editable: false },
  { name: "performedByType", dataType: DataType.STRING, additionalField: false, editable: false },
  // The device that scanned the row — joined server-side from `performedBy`
  // (only app rows point at a device; cockpit/automatic rows stay blank).
  // Filterable and sortable: the backend joins it BEFORE its $match/$sort.
  { name: "deviceName", dataType: DataType.STRING, additionalField: false, editable: false },
  // Session check-in (2026-07-22): denormalized agenda-item title on
  // SESSION_CHECK_IN rows (backend NATIVE_LOG_FIELDS carries programItemId
  // too, so filters on it stay index-backed server-side).
  { name: "programItemName", dataType: DataType.STRING, additionalField: false, editable: false },
  { name: "note", dataType: DataType.STRING, additionalField: false, editable: false },
  { name: "checkInLogCTA", dataType: DataType.CHECK_IN_LOG_CTA, additionalField: false, editable: false },
];

export const INVALID_STRING_PATTERN =
  /[\u{1F000}-\u{1F9FF}\u{2600}-\u{27BF}\u0000-\u001F\u007F-\u009F]/u;

export const VALID_LONG_PATTERN = /^-?\d+$/;
export const MAX_LONG_LENGTH = 20;

export const VALID_DOUBLE_PATTERN = /^-?\d*\.?\d*$/;
export const MAX_DOUBLE_LENGTH = 32;

// The ONE rule for a deliverable address (`src/lib/email-rule.ts`, twin of
// main-backend `EmailRule` and main-consumer; plan "Saubere Eingaben"
// 2026-10-05): ASCII before the "@", an LDH domain with a letter-led ending
// of two or more characters, 6–254 in total. Compiled from the rule's source
// here — never a copy of the string — and WITHOUT flags (see email-rule.ts).
// The old shape `^[^@\s]+@[^@\s]+\.[^@\s]+$` knew neither U+200E nor U+200B,
// which is how an address SES refuses was saved and sent.
export const VALID_EMAIL_PATTERN = new RegExp(EMAIL_RULE_PATTERN_SOURCE);
export const MAX_EMAIL_LENGTH = EMAIL_RULE_MAX_LENGTH;

export const FIELDS_EVENT: Field[] = [
  {
    dataType: DataType.ID,
    name: "id",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.CTA,
    name: "cta",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.STRING,
    name: "name",
    additionalField: false,
    editable: true,
  },
  {
    dataType: DataType.SINGLE_SELECT,
    name: "registrationType",
    additionalField: false,
    options: ["INVITATION", "LOCKED", "OPEN"],
    required: true,
    editable: true,
  },
  {
    dataType: DataType.STRING,
    name: "companyName",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.STRING,
    name: "locationName",
    additionalField: false,
    editable: true,
  },
  {
    dataType: DataType.STRING,
    name: "address",
    additionalField: false,
    editable: true,
  },
  {
    dataType: DataType.STRING,
    name: "address2",
    additionalField: false,
    editable: true,
  },
  {
    dataType: DataType.STRING,
    name: "zipCode",
    additionalField: false,
    editable: true,
  },
  {
    dataType: DataType.STRING,
    name: "city",
    additionalField: false,
    editable: true,
  },
  {
    dataType: DataType.STRING,
    name: "country",
    additionalField: false,
    editable: true,
  },
  {
    dataType: DataType.LONG,
    name: "guestsTotal",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.LONG,
    name: "guestsAccepted",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.LONG,
    name: "guestsDeclined",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.LONG,
    name: "guestsContacted",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.LONG,
    name: "guestsWaitlist",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.LONG,
    name: "guestsCheckedIn",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.LONG,
    name: "guestsUncontacted",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.DATE_TIME,
    name: "start",
    additionalField: false,
    editable: true,
  },
  {
    dataType: DataType.DATE_TIME,
    name: "end",
    additionalField: false,
    editable: true,
  },
  {
    dataType: DataType.SINGLE_SELECT,
    name: "timeZone",
    additionalField: false,
    options: TIMEZONE_OPTIONS,
    required: true,
    editable: true,
  },
  {
    dataType: DataType.DATE_TIME,
    name: "createdAt",
    additionalField: false,
    editable: false,
  },
];

export const FIELDS_EMAIL_SENT: Field[] = [
  // Audit 2026-09-12 — four columns removed, all for the same reason: the cell
  // could never carry a value, and a column that cannot fill is worse than a
  // missing one. It reads as "this mail has no sender id", not as "we do not
  // ship this".
  //
  //   numCompanions, companions  — mapped on the DTO, never projected
  //   senderMailId               — the sharpest of them: it IS in the Mongo
  //                                projection, but the Java entity never
  //                                learned the field and the row mapper has no
  //                                arm for it, so it read null on every row
  //                                while looking wired from every other angle.
  //                                It WAS a working filter key, and a saved
  //                                filter on it would now be dropped without a
  //                                banner (`parseFiltersApiRequest`, drop mode)
  //                                — checked against prod on 2026-09-12: 0 of
  //                                29 saved filters and 0 of 31 saved views
  //                                mention it, so nothing is orphaned.
  //   html                       — the backend WOULD serve it; this table
  //                                deliberately never asks (it is a full
  //                                rendered email per row, and the detail popup
  //                                fetches the body itself)
  //
  // `emailSent-fields.imageUrl` also exists as a locale key, but there was
  // never a field for it here — that one is an orphan string, not a column.
  // `status` has no label in any bundle at all.
  {
    dataType: DataType.OBJECT_ID,
    name: "guestId",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.OBJECT_ID,
    name: "eventId",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.SINGLE_SELECT,
    name: "guestType",
    additionalField: false,
    options: ["mainGuest", "companion"],
    editable: false,
  },
  {
    dataType: DataType.SINGLE_SELECT,
    name: "mainGuest",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.GUEST_STATUS,
    name: "state",
    additionalField: false,
    options: ["UNCONTACTED", "CONTACTED", "ACCEPTED", "DECLINED", "WAITLISTED"],
    editable: false,
  },
  {
    dataType: DataType.SINGLE_SELECT,
    name: "acceptanceState",
    additionalField: false,
    options: ["ACCEPTED", "DECLINED", "PENDING", "SYSTEM"],
    editable: false,
  },
  {
    dataType: DataType.SINGLE_SELECT,
    name: "gender",
    additionalField: false,
    options: ["M", "F", "D"],
    editable: false,
  },
  {
    dataType: DataType.STRING,
    name: "title",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.STRING,
    name: "firstName",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.STRING,
    name: "lastName",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.STRING,
    name: "company",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.STRING,
    name: "jobTitle",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.STRING,
    name: "address",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.STRING,
    name: "address2",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.STRING,
    name: "zipCode",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.BOOLEAN,
    name: "archived",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.STRING,
    name: "city",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.STRING,
    name: "country",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.EMAIL,
    name: "email",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.STRING,
    name: "phone",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.STRING,
    name: "mobile",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.SINGLE_SELECT,
    name: "language",
    additionalField: false,
    options: ALL_LANGUAGE_OPTIONS,
    editable: false,
  },
  {
    dataType: DataType.STRING,
    name: "username",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.STRING,
    name: "code",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.BOOLEAN,
    name: "checkedIn",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.DATE_TIME,
    name: "checkedInAt",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.DATE_TIME,
    name: "createdAt",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.DATE_TIME,
    name: "updatedAt",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.BOOLEAN,
    name: "emailOptIn",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.ID,
    name: "emailSentId",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.EMAIL,
    name: "senderMail",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.EMAIL,
    name: "replyToMail",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.EMAIL,
    name: "ccMail",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.EMAIL,
    name: "bccMail",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.STRING,
    name: "mailSubject",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.EMAIL,
    name: "recipientMail",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.STRING,
    name: "templateName",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.STRING,
    name: "sendName",
    additionalField: false,
    editable: false,
  },
  {
    // W4-3 / 4e: the row's send class. MARKETING is the absent value (every
    // row before the column existed), ORGANISATIONAL the attested notice,
    // STAFF_NOTICE the arrival mail to a verified staff contact — the filter
    // option that separates the notices from the guest's own mails. Labels
    // come from the `sendClassOptions` catalogue (optionNamespace), the same
    // mechanism `source` uses on the action-log table.
    dataType: DataType.SINGLE_SELECT,
    name: "sendClass",
    additionalField: false,
    editable: false,
    options: ["MARKETING", "ORGANISATIONAL", "STAFF_NOTICE"],
    optionNamespace: "sendClassOptions",
  },
  {
    dataType: DataType.EMAIL_STATUS,
    options: [
      "SUCCESS",
      "SENT",
      "DELIVERED",
      "BOUNCED",
      "COMPLAINED",
      "REJECTED",
      "FAILED",
      "DELAYED",
      "PENDING",
      "SKIPPED",
      "OPEN",
      "CLICK",
      "SOFT_BOUNCED",
    ],
    name: "emailStatus",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.DATE_TIME,
    name: "sentAt",
    additionalField: false,
    editable: false,
  },
  // W4-e. Three timestamps of the MAIL row (parser click lanes + the
  // portal-backend visit stamp) and two of the GUEST (joined; the CUSTOM
  // allowlist applies to those two like to every other guest column).
  // `websiteLoginAt` is the LAST login and `portalVisitedAt` the FIRST
  // logged-in website visit — guest-level facts, which is why their labels
  // are the guest list's own and no "after this mail" column is derived.
  {
    dataType: DataType.DATE_TIME,
    name: "humanClickAt",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.DATE_TIME,
    name: "machineClickAt",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.DATE_TIME,
    name: "portalVisitedViaMailAt",
    additionalField: false,
    editable: false,
  },
  // W4-e ⚑I: the website twin — reached the event website through THIS mail.
  {
    dataType: DataType.DATE_TIME,
    name: "websiteVisitedViaMailAt",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.DATE_TIME,
    name: "websiteLoginAt",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.DATE_TIME,
    name: "portalVisitedAt",
    additionalField: false,
    editable: false,
  },
];

export const FIELDS_EVENT_LIMITS: Field[] = [
  {
    dataType: DataType.EVENT_LIMIT_META,
    name: "name",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.LONG,
    name: "blocked",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.LIMITS,
    name: "currentCollaboratorCount",
    additionalField: false,
    editable: false,
  },
];

export const FIELDS_GUEST: Field[] = [
  {
    dataType: DataType.OBJECT_ID,
    name: "guestId",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.SINGLE_SELECT,
    name: "guestType",
    additionalField: false,
    options: ["mainGuest", "companion"],
    editable: true,
  },
  {
    dataType: DataType.ID,
    name: "id",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.STRING,
    name: "firstName",
    additionalField: false,
    editable: true,
  },
  {
    dataType: DataType.STRING,
    name: "lastName",
    additionalField: false,
    editable: true,
  },
  {
    dataType: DataType.SINGLE_SELECT,
    name: "gender",
    additionalField: false,
    options: ["M", "F", "D"],
    editable: true,
  },
  {
    dataType: DataType.STRING,
    name: "title",
    additionalField: false,
    editable: true,
  },
  {
    dataType: DataType.STRING,
    name: "jobTitle",
    additionalField: false,
    editable: true,
  },
  {
    dataType: DataType.STRING,
    name: "company",
    additionalField: false,
    editable: true,
  },
  {
    dataType: DataType.EMAIL,
    name: "email",
    additionalField: false,
    editable: true,
  },
  {
    dataType: DataType.STRING,
    name: "phone",
    additionalField: false,
    editable: true,
  },
  {
    dataType: DataType.STRING,
    name: "mobile",
    additionalField: false,
    editable: true,
  },
  {
    dataType: DataType.STRING,
    name: "address",
    additionalField: false,
    editable: true,
  },
  {
    dataType: DataType.STRING,
    name: "address2",
    additionalField: false,
    editable: true,
  },
  {
    dataType: DataType.STRING,
    name: "zipCode",
    additionalField: false,
    editable: true,
  },
  {
    dataType: DataType.STRING,
    name: "city",
    additionalField: false,
    editable: true,
  },
  {
    dataType: DataType.STRING,
    name: "country",
    additionalField: false,
    editable: true,
  },
  {
    dataType: DataType.SINGLE_SELECT,
    name: "language",
    additionalField: false,
    options: ALL_LANGUAGE_OPTIONS,
    editable: true,
  },
  {
    dataType: DataType.BOOLEAN,
    name: "emailOptIn",
    additionalField: false,
    required: false,
    editable: true,
  },
  {
    // SMS channel wave: the SMS consent twin of `emailOptIn` (strict: a guest
    // SMS needs exactly `true`). Shown only while the backend switch is on —
    // FieldsProvider drops it otherwise (`withoutSmsFieldsUnless`).
    dataType: DataType.BOOLEAN,
    name: "smsOptIn",
    additionalField: false,
    required: false,
    editable: true,
  },
  {
    dataType: DataType.STRING,
    name: "username",
    additionalField: false,
    editable: true,
  },
  {
    dataType: DataType.STRING,
    name: "code",
    additionalField: false,
    editable: true,
  },
  {
    // DERIVED, read-only: base64url("username:code") — the value the mail
    // lane puts after `?credentials=` in a personal login link, so a mail
    // merge can build that link itself. The backend computes it
    // (org.nikita.utils.GuestLoginToken) whenever the projection asks for it;
    // nothing stores it, which is why it is excluded from the condition
    // builders (EXCLUDED_CONDITION_FIELD_NAMES) and from sort/group — Mongo
    // has no field to query or order by, and a filter on it would silently
    // match nobody.
    dataType: DataType.STRING,
    name: "loginToken",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.GUEST_STATUS,
    name: "state",
    additionalField: false,
    options: ["UNCONTACTED", "CONTACTED", "ACCEPTED", "DECLINED", "WAITLISTED"],
    required: true,
    editable: true,
  },
  {
    dataType: DataType.CHECK_IN_STATUS,
    name: "checkedIn",
    additionalField: false,
    options: ["checkedIn", "checkedOut"],
    editable: false,
  },
  {
    dataType: DataType.DATE_TIME,
    name: "checkedInAt",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.CHECK_IN_OUT,
    name: "checkInOutCTA",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.LONG,
    name: "numCompanions",
    additionalField: false,
    editable: false,
  },
  // Provenance ("Source"): how the guest entered the system. Static SINGLE_SELECT
  // whose option values are the GuestOrigin enum (labels via optionsMultiSingleSelect
  // i18n). Emits `{ key: 'origin', op: '=', value: <ENUM> }`; the `∅` operator on it
  // is the "Unknown" (legacy, origin unset) pick. IMPORT_CSV is the generic
  // "all imports" pick — a SPECIFIC import batch is picked via `importId` below.
  {
    dataType: DataType.SINGLE_SELECT,
    name: "origin",
    additionalField: false,
    // Options ARE the GuestOrigin enum (labels via optionsMultiSingleSelect
    // i18n) — derived so a new origin can never be forgotten here.
    options: Object.values(GuestOrigin),
    editable: false,
  },
  // Specific CSV import batch. Dynamic optionSource: FieldsProvider fills `options`
  // (the batch hex ids) + `optionLabels` (id → batch name) from
  // GET /guests/{eventId}/imports. Emits `{ key: 'importId', op: '=', value: <hex> }`.
  {
    dataType: DataType.SINGLE_SELECT,
    name: "importId",
    additionalField: false,
    optionSource: "imports",
    editable: false,
  },
  // Wave B: backlink to the event speaker this guest was promoted from (hex
  // STRING of event_speakers._id — string equality works in every filter
  // lane). The "is a promoted speaker" marker IS this field: filter with ⊙
  // (set) / ∅ (empty). Written only by the promote endpoint + delete
  // cascades → not editable; NOT in any default visible column set.
  {
    dataType: DataType.STRING,
    name: "speakerId",
    additionalField: false,
    editable: false,
  },
  // Wave L: sponsor↔guests marker (hex STRING of event_sponsors._id) — "this
  // guest belongs to sponsor X". Written only by the sponsor link/unlink
  // endpoints + delete cascade → not editable; filter with ⊙/∅/=.
  {
    dataType: DataType.STRING,
    name: "sponsorId",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.DATE_TIME,
    name: "createdAt",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.DATE_TIME,
    name: "updatedAt",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.BOOLEAN,
    name: "archived",
    additionalField: false,
    // W3-G: archived is user-togglable on the guest list (archive/unarchive),
    // so the column must be editable — it was flagged read-only.
    editable: true,
  },
  {
    dataType: DataType.COMPANIONS,
    name: "companions",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.ATTACHMENTS,
    name: "attachments",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.SLOTTING,
    name: "slotting",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.SEATING,
    name: "seating",
    additionalField: false,
    editable: false,
  },
  // ---- Activity rollups (denormalized; filter-only, derived → not editable) ----
  // Backend: GuestActivityRollupService + ALLOWED_GUEST_FIELDS. Tier-2 ObjectId
  // arrays (apptOfferingIds/seatRoomIds/emailedTemplateIds) ARE listed below and
  // resolve to a dynamic id→name picker via Field.optionSource + FieldsProvider
  // (apptTreatmentIds is backend-only — deferred, per-offering N+1 fetch).
  {
    dataType: DataType.MULTI_SELECT,
    name: "apptStatuses",
    additionalField: false,
    options: ["TENTATIVE", "CONFIRMED", "CHECKED_IN", "CANCELLED"],
    editable: false,
  },
  {
    dataType: DataType.LONG,
    name: "apptCount",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.DATE_TIME,
    name: "nextApptAt",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.BOOLEAN,
    name: "hasOpenRequest",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.MULTI_SELECT,
    name: "seatStatuses",
    additionalField: false,
    options: ["unassigned", "assigned", "tentative", "reserved", "checkedIn"],
    editable: false,
  },
  {
    dataType: DataType.LONG,
    name: "seatCount",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.DATE_TIME,
    name: "lastEmailedAt",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.EMAIL_STATUS,
    name: "lastEmailStatus",
    additionalField: false,
    // EMAIL_STATUS cells render options from this list (mirror FIELDS_EMAIL_SENT
    // emailStatus) — without it the filter dropdown would be empty/unselectable.
    options: [
      "SUCCESS",
      "SENT",
      "DELIVERED",
      "BOUNCED",
      "COMPLAINED",
      "REJECTED",
      "FAILED",
      "DELAYED",
      "PENDING",
      "SKIPPED",
      "OPEN",
      "CLICK",
      "SOFT_BOUNCED",
    ],
    editable: false,
  },
  {
    dataType: DataType.DATE_TIME,
    name: "lastEmailOpenedAt",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.DATE_TIME,
    name: "lastEmailClickedAt",
    additionalField: false,
    editable: false,
  },
  // PB-TH-7: bounced-address suppression. Raised ONLY by ses-notifications-parser
  // (permanent bounce or abusive complaint); main-consumer checks it BEFORE the
  // consent gate, so while it stands the guest receives nothing at all. The
  // only ways down are the notice in the guest detail (DELETE
  // clearEmailUndeliverable) and a corrected address — there is no HTTP write
  // that raises it, and the guest-update diff has no branch for it, hence
  // `editable:false`. Until these two entries existed the flag was visible in
  // the detail popup and the export but had no guest-list column and no
  // filter, so "who is blocked?" was a hand-written Mongo query. Both keys
  // also ride the list projection (GuestHandling.GUEST_ROLLUP_FIELDS) — a
  // catalogue column the projection omits renders empty for every guest.
  {
    dataType: DataType.BOOLEAN,
    name: "emailUndeliverable",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.DATE_TIME,
    name: "emailUndeliverableAt",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.DATE_TIME,
    name: "portalVisitedAt",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.DATE_TIME,
    name: "websiteLoginAt",
    additionalField: false,
    editable: false,
  },
  // ---- Tier-2 activity rollups: dynamic ObjectId options (id→name picker) ----
  // `options` + `optionLabels` are filled per-event by FieldsProvider from the
  // optionSource fetch; stored values are hex ids (membership filter, same as a
  // custom MULTI_SELECT). Powers "appointments in offering X" / "seat in room Y".
  {
    dataType: DataType.MULTI_SELECT,
    name: "apptOfferingIds",
    additionalField: false,
    optionSource: "offerings",
    editable: false,
  },
  {
    dataType: DataType.MULTI_SELECT,
    name: "apptTreatmentIds",
    additionalField: false,
    optionSource: "treatments",
    editable: false,
  },
  {
    dataType: DataType.MULTI_SELECT,
    name: "apptLaneIds",
    additionalField: false,
    optionSource: "lanes",
    editable: false,
  },
  {
    dataType: DataType.MULTI_SELECT,
    name: "seatRoomIds",
    additionalField: false,
    optionSource: "rooms",
    editable: false,
  },
  {
    dataType: DataType.MULTI_SELECT,
    name: "emailedTemplateIds",
    additionalField: false,
    optionSource: "emailTemplates",
    editable: false,
  },
  {
    // Email campaigns the guest received. Value === label (the free-text
    // sendName); options come from sent_emails distinct sendName per event.
    dataType: DataType.MULTI_SELECT,
    name: "emailedSendNames",
    additionalField: false,
    optionSource: "campaigns",
    editable: false,
  },
];

/**
 * Contact core-field names — the subset of guest built-ins a CRM contact
 * shares. FIELDS_CONTACT is DERIVED from FIELDS_GUEST via this allowlist (not
 * hand-written) so dataType/options/label matching stays byte-identical with
 * the guest column (mapper + `guest-fields` i18n keys keep working — contact
 * field labels resolve through the same names).
 */
const CONTACT_CORE_FIELD_NAMES = [
  "firstName",
  "lastName",
  "email",
  "phone",
  "mobile",
  "company",
  "jobTitle",
  "address",
  "address2",
  "zipCode",
  "city",
  "country",
  "gender",
  "title",
  "language",
  "createdAt",
  "updatedAt",
];

/**
 * Built-in columns of the CRM contact pool table. Shallow copies of the
 * matching FIELDS_GUEST entries (guaranteed ⊆ FIELDS_GUEST by construction,
 * FIELDS_GUEST order preserved) so a later per-event mutation (e.g.
 * FieldsProvider filling optionLabels) can never bleed between the tables.
 * createdAt/updatedAt stay non-editable; everything else keeps the guest
 * column's editability.
 */
export const FIELDS_CONTACT: Field[] = FIELDS_GUEST.filter((field) =>
  CONTACT_CORE_FIELD_NAMES.includes(field.name),
).map((field) => ({ ...field }));

/**
 * Default visible columns of the contact table (the literal DEFAULT view —
 * contacts have no saved views in v1). Names must be ⊆ FIELDS_CONTACT.
 */
export const DEFAULT_CONTACT_FIELDS = [
  "firstName",
  "lastName",
  "email",
  "company",
  "jobTitle",
  "city",
];

/**
 * Built-in columns of the speaker hub DataTable (Wave A). All read-only in
 * the grid (no inline edit v1 — editing goes through the speaker modal).
 * `socials` (link count) is UNKNOWN on purpose: it renders via a dedicated
 * branch in SpeakersRows and must stay out of the typed filter/sort surfaces
 * (the backend has no socials filter lane). `imageUrl` keeps IMAGE so it is
 * excluded from sorting; the cell renders the existing avatar img.
 */
export const FIELDS_SPEAKER: Field[] = [
  {
    dataType: DataType.LONG,
    name: "sortIndex",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.IMAGE,
    name: "imageUrl",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.STRING,
    name: "name",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.STRING,
    name: "occupation",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.STRING,
    name: "company",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.EMAIL,
    name: "email",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.STRING,
    name: "phone",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.STRING,
    name: "tags",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.UNKNOWN,
    name: "socials",
    additionalField: false,
    editable: false,
  },
  // Wave L: sponsor↔speaker marker (hex STRING of event_sponsors._id) —
  // "this speaker belongs to sponsor X". Written only by the link-sponsor /
  // unlink-sponsor endpoints + sponsor-delete cascade → not editable; filter
  // with ⊙/∅/=; NOT in any default visible column set.
  {
    dataType: DataType.STRING,
    name: "sponsorId",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.DATE_TIME,
    name: "createdAt",
    additionalField: false,
    editable: false,
  },
];

/**
 * Built-in columns of the sponsor hub DataTable (Wave A). Read-only in the
 * grid. `tier` is a SINGLE_SELECT over the canonical tier suggestions so the
 * typed filter offers a picker — the backend stores a free string, and a
 * custom tier value still displays raw in the cell (non-strict resolution).
 */
export const FIELDS_SPONSOR: Field[] = [
  {
    dataType: DataType.LONG,
    name: "sortIndex",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.IMAGE,
    name: "logoUrl",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.STRING,
    name: "name",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.SINGLE_SELECT,
    name: "tier",
    additionalField: false,
    options: ["PLATINUM", "GOLD", "SILVER", "BRONZE"],
    editable: false,
  },
  {
    dataType: DataType.URL,
    name: "linkUrl",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.STRING,
    name: "contactName",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.EMAIL,
    name: "contactEmail",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.STRING,
    name: "tags",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.STRING,
    name: "description",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.DATE_TIME,
    name: "createdAt",
    additionalField: false,
    editable: false,
  },
];

export const FIELDS_REGISTRANT: Field[] = [
  {
    dataType: DataType.SINGLE_SELECT,
    name: "guestType",
    additionalField: false,
    options: ["mainGuest", "companion"],
    editable: true,
  },
  {
    dataType: DataType.OBJECT_ID,
    name: "guestId",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.ID,
    name: "id",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.STRING,
    name: "firstName",
    additionalField: false,
    editable: true,
  },
  {
    dataType: DataType.STRING,
    name: "lastName",
    additionalField: false,
    editable: true,
  },
  {
    dataType: DataType.SINGLE_SELECT,
    name: "gender",
    additionalField: false,
    options: ["M", "F", "D"],
    editable: true,
  },
  {
    dataType: DataType.STRING,
    name: "title",
    additionalField: false,
    editable: true,
  },
  {
    dataType: DataType.STRING,
    name: "jobTitle",
    additionalField: false,
    editable: true,
  },
  {
    dataType: DataType.STRING,
    name: "company",
    additionalField: false,
    editable: true,
  },
  {
    dataType: DataType.EMAIL,
    name: "email",
    additionalField: false,
    editable: true,
  },
  {
    dataType: DataType.STRING,
    name: "phone",
    additionalField: false,
    editable: true,
  },
  {
    dataType: DataType.STRING,
    name: "mobile",
    additionalField: false,
    editable: true,
  },
  {
    dataType: DataType.STRING,
    name: "address",
    additionalField: false,
    editable: true,
  },
  {
    dataType: DataType.STRING,
    name: "address2",
    additionalField: false,
    editable: true,
  },
  {
    dataType: DataType.STRING,
    name: "zipCode",
    additionalField: false,
    editable: true,
  },
  {
    dataType: DataType.STRING,
    name: "city",
    additionalField: false,
    editable: true,
  },
  {
    dataType: DataType.STRING,
    name: "country",
    additionalField: false,
    editable: true,
  },
  {
    dataType: DataType.SINGLE_SELECT,
    name: "language",
    additionalField: false,
    options: ALL_LANGUAGE_OPTIONS,
    editable: true,
  },
  {
    dataType: DataType.BOOLEAN,
    name: "emailOptIn",
    additionalField: false,
    required: false,
    editable: true,
  },
  {
    // SMS channel wave: the SMS consent twin of `emailOptIn` (strict: a guest
    // SMS needs exactly `true`). Shown only while the backend switch is on —
    // FieldsProvider drops it otherwise (`withoutSmsFieldsUnless`).
    dataType: DataType.BOOLEAN,
    name: "smsOptIn",
    additionalField: false,
    required: false,
    editable: true,
  },
  {
    dataType: DataType.STRING,
    name: "username",
    additionalField: false,
    editable: true,
  },
  {
    dataType: DataType.STRING,
    name: "code",
    additionalField: false,
    editable: true,
  },
  {
    // DERIVED, read-only: base64url("username:code") — the value the mail
    // lane puts after `?credentials=` in a personal login link, so a mail
    // merge can build that link itself. The backend computes it
    // (org.nikita.utils.GuestLoginToken) whenever the projection asks for it;
    // nothing stores it, which is why it is excluded from the condition
    // builders (EXCLUDED_CONDITION_FIELD_NAMES) and from sort/group — Mongo
    // has no field to query or order by, and a filter on it would silently
    // match nobody.
    dataType: DataType.STRING,
    name: "loginToken",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.GUEST_STATUS,
    name: "state",
    additionalField: false,
    options: ["UNCONTACTED", "CONTACTED", "ACCEPTED", "DECLINED", "WAITLISTED"],
    required: true,
    editable: true,
  },
  {
    dataType: DataType.CHECK_IN_STATUS,
    name: "checkedIn",
    additionalField: false,
    options: ["checkedIn", "checkedOut"],
    editable: false,
  },
  {
    dataType: DataType.DATE_TIME,
    name: "checkedInAt",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.LONG,
    name: "numCompanions",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.DATE_TIME,
    name: "createdAt",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.DATE_TIME,
    name: "updatedAt",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.BOOLEAN,
    name: "archived",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.ACTION,
    name: "actionRegistrant",
    additionalField: false,
    editable: false,
  },
  // NO `companions` ("Verknüpfung") column here, and the reason is in the
  // backend: the `$lookup` that fills the array is gated on
  // `acceptanceState ∈ {ACCEPTED, SYSTEM}` or `state == WAITLISTED`
  // (`GuestRepository.activeCompanionPredicate`). This lane lists PENDING
  // registrations, whose plus-ones are usually PENDING too — measured: all
  // pending → the cell is EMPTY, one of two approved → it shows ONE. A column
  // that hides companions inside an accept/decline decision is worse than no
  // column. The companions tab resolves the link through a service-level join
  // without that filter, which is why it can offer it.
];

export const FIELDS_COMPANION: Field[] = [
  {
    dataType: DataType.SINGLE_SELECT,
    name: "guestType",
    additionalField: false,
    options: ["mainGuest", "companion"],
    editable: true,
  },
  {
    dataType: DataType.OBJECT_ID,
    name: "guestId",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.ID,
    name: "id",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.STRING,
    name: "firstName",
    additionalField: false,
    editable: true,
  },
  {
    dataType: DataType.STRING,
    name: "lastName",
    additionalField: false,
    editable: true,
  },
  {
    dataType: DataType.SINGLE_SELECT,
    name: "gender",
    additionalField: false,
    options: ["M", "F", "D"],
    editable: true,
  },
  {
    dataType: DataType.STRING,
    name: "title",
    additionalField: false,
    editable: true,
  },
  {
    dataType: DataType.STRING,
    name: "jobTitle",
    additionalField: false,
    editable: true,
  },
  {
    dataType: DataType.STRING,
    name: "company",
    additionalField: false,
    editable: true,
  },
  {
    dataType: DataType.EMAIL,
    name: "email",
    additionalField: false,
    editable: true,
  },
  {
    dataType: DataType.STRING,
    name: "phone",
    additionalField: false,
    editable: true,
  },
  {
    dataType: DataType.STRING,
    name: "mobile",
    additionalField: false,
    editable: true,
  },
  {
    dataType: DataType.STRING,
    name: "address",
    additionalField: false,
    editable: true,
  },
  {
    dataType: DataType.STRING,
    name: "address2",
    additionalField: false,
    editable: true,
  },
  {
    dataType: DataType.STRING,
    name: "zipCode",
    additionalField: false,
    editable: true,
  },
  {
    dataType: DataType.STRING,
    name: "city",
    additionalField: false,
    editable: true,
  },
  {
    dataType: DataType.STRING,
    name: "country",
    additionalField: false,
    editable: true,
  },
  {
    dataType: DataType.SINGLE_SELECT,
    name: "language",
    additionalField: false,
    options: ALL_LANGUAGE_OPTIONS,
    editable: true,
  },
  {
    dataType: DataType.BOOLEAN,
    name: "emailOptIn",
    additionalField: false,
    required: false,
    editable: true,
  },
  {
    dataType: DataType.STRING,
    name: "username",
    additionalField: false,
    editable: true,
  },
  {
    dataType: DataType.STRING,
    name: "code",
    additionalField: false,
    editable: true,
  },
  {
    // DERIVED, read-only: base64url("username:code") — the value the mail
    // lane puts after `?credentials=` in a personal login link, so a mail
    // merge can build that link itself. The backend computes it
    // (org.nikita.utils.GuestLoginToken) whenever the projection asks for it;
    // nothing stores it, which is why it is excluded from the condition
    // builders (EXCLUDED_CONDITION_FIELD_NAMES) and from sort/group — Mongo
    // has no field to query or order by, and a filter on it would silently
    // match nobody.
    dataType: DataType.STRING,
    name: "loginToken",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.GUEST_STATUS,
    name: "state",
    additionalField: false,
    options: ["UNCONTACTED", "CONTACTED", "ACCEPTED", "DECLINED", "WAITLISTED"],
    required: true,
    editable: true,
  },
  {
    dataType: DataType.CHECK_IN_STATUS,
    name: "checkedIn",
    additionalField: false,
    options: ["checkedIn", "checkedOut"],
    editable: false,
  },
  {
    dataType: DataType.DATE_TIME,
    name: "checkedInAt",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.DATE_TIME,
    name: "createdAt",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.DATE_TIME,
    name: "updatedAt",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.BOOLEAN,
    name: "archived",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.ACTION,
    name: "actionCompanion",
    additionalField: false,
    editable: false,
  },
  {
    dataType: DataType.COMPANIONS,
    name: "companions",
    additionalField: false,
    editable: false,
  },
];

// GIF joined for the guest portal's loading animation — the bytes land in R2
// untouched, so an animated file stays animated. Mirrors the backend's
// R2UploadGuard.ALLOWED_IMAGE_TYPES (the presign lane rejects anything else).
export const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/gif"] as const;
export const ACCEPTED_IMAGE_EXTENSIONS = ".jpg, .jpeg, .png, .gif";
// The Open Graph image lane measures the file with ImageIO on the server
// (R2Resource.uploadOgImage: "Image must be PNG or JPEG") — it must NOT
// inherit GIF from the list above, or the picker offers a type the backend
// refuses with an error that blames the dimensions.
export const OG_IMAGE_TYPES = ["image/jpeg", "image/png"] as const;
// The iOS home-screen icon (apple-touch-icon) is PNG for Safari and JPEG at
// best — a GIF there is silently ignored, no icon and no error. Same values as
// OG_IMAGE_TYPES, kept a separate name because the REASON differs: narrowing
// or widening one must never quietly move the other.
export const TOUCH_ICON_IMAGE_TYPES = ["image/jpeg", "image/png"] as const;

export const VERIFICATION_ROUTE = "/auth/verification";

export const CHANGE_PASSWORD_ROUTE = "/auth/change-password";

export const CHECK_EMAIL_ROUTE = "/auth/check-email";

export const ACTIVATE_ROUTE = "/auth/activate";

// Define routes where authentication is not required
export const AUTH_ROUTES = [
  CHANGE_PASSWORD_ROUTE,
  CHECK_EMAIL_ROUTE,
  "/auth/login",
  "/auth/register",
  "/auth/reset-password",
  "/auth/mfa-verify",
  "/auth/mfa-setup",
];

export const LANGUAGE_CODES: readonly string[] = locales;

export const FALLBACK_LANGUAGE = "de-DE";

/**
 * Every built-in column name the GUEST FAMILY knows — guest list, registrations
 * and companions together.
 *
 * The three surfaces share ONE saved-view pool (`getTableViewTypeFromViewFilterType`
 * maps all of them to `TableViewType.GUEST`) but NOT one catalogue: `FIELDS_GUEST`
 * carries ~56 names, the two request catalogues ~31 each. So "this surface has no
 * such field" and "this field no longer exists" are different questions, and
 * anything deciding whether a saved view still names a LIVE column has to ask the
 * second one — against this union.
 */
export const GUEST_FAMILY_FIELD_NAMES: ReadonlySet<string> = new Set(
  [...FIELDS_GUEST, ...FIELDS_REGISTRANT, ...FIELDS_COMPANION].map((f) => f.name),
);

export function defaultView(
  type: "event" | "guest" | "registrant" | "companion" | "emailSent" | "checkIn" | "eventLimits" | "actionLog" | "contact" | "speaker" | "sponsor",
): TableView {
  return {
    tableViewId: "DEFAULT",
    name: "DEFAULT",
    userId: "",
    fields:
      type === "event"
        ? DEFAULT_EVENT_FIELDS
        : type === "guest"
          ? DEFAULT_GUEST_FIELDS
          : type === "checkIn"
            ? DEFAULT_CHECK_IN_FIELDS
            : type === "companion"
              ? DEFAULT_COMPANIONS_FIELDS
              : type === "emailSent"
                ? DEFAULT_EMAIL_SENT_FIELDS
                : type === "actionLog"
                  ? DEFAULT_ACTION_LOG_FIELDS
                  : type === "contact"
                    ? DEFAULT_CONTACT_FIELDS
                    : type === "speaker"
                      ? DEFAULT_SPEAKER_FIELDS
                      : type === "sponsor"
                        ? DEFAULT_SPONSOR_FIELDS
                        : DEFAULT_REGISTER_FIELDS,
  };
}

export function defaultFilter(): TableFilter {
  return {
    tableFilterId: "DEFAULT",
    name: "DEFAULT",
    userId: "",
    filter: [], // Initialize as empty array
  };
}

export const htmlLanguages = [
  "af", // Afrikaans
  "sq", // Albanian
  "am", // Amharic
  "ar", // Arabic
  "ar-SA", // Arabic (Saudi Arabia)
  "ar-EG", // Arabic (Egypt)
  "ar-MA", // Arabic (Morocco)
  "hy", // Armenian
  "az", // Azerbaijani
  "eu", // Basque
  "be", // Belarusian
  "bn", // Bengali
  "bs", // Bosnian
  "bg", // Bulgarian
  "my", // Burmese
  "ca", // Catalan
  "zh", // Chinese (generic)
  "zh-CN", // Chinese (Simplified)
  "zh-TW", // Chinese (Traditional)
  "zh-HK", // Chinese (Hong Kong)
  "hr", // Croatian
  "cs", // Czech
  "da", // Danish
  "nl", // Dutch
  "nl-BE", // Dutch (Belgium / Flemish)
  "en", // English (generic)
  "en-GB", // English (United Kingdom)
  "en-US", // English (United States)
  "en-CA", // English (Canada)
  "en-AU", // English (Australia)
  "en-IN", // English (India)
  "et", // Estonian
  "fi", // Finnish
  "fr", // French
  "fr-BE", // French (Belgium)
  "fr-CA", // French (Canada)
  "fr-CH", // French (Switzerland)
  "ka", // Georgian
  "de", // German
  "de-AT", // German (Austria)
  "de-CH", // German (Switzerland)
  "el", // Greek
  "gu", // Gujarati
  "ht", // Haitian Creole
  "ha", // Hausa
  "he", // Hebrew
  "hi", // Hindi
  "hu", // Hungarian
  "is", // Icelandic
  "id", // Indonesian
  "ga", // Irish
  "it", // Italian
  "ja", // Japanese
  "jv", // Javanese
  "kn", // Kannada
  "kk", // Kazakh
  "km", // Khmer
  "ko", // Korean
  "ky", // Kyrgyz
  "lo", // Lao
  "lv", // Latvian
  "lt", // Lithuanian
  "lb", // Luxembourgish
  "mk", // Macedonian
  "ms", // Malay
  "ml", // Malayalam
  "mt", // Maltese
  "mr", // Marathi
  "mn", // Mongolian
  "ne", // Nepali
  "no", // Norwegian
  "nb", // Norwegian Bokmål
  "nn", // Norwegian Nynorsk
  "fa", // Persian (Farsi)
  "pl", // Polish
  "pt", // Portuguese (generic)
  "pt-PT", // Portuguese (Portugal)
  "pt-BR", // Portuguese (Brazil)
  "pa", // Punjabi
  "ro", // Romanian
  "ru", // Russian
  "sr", // Serbian
  "si", // Sinhala
  "sk", // Slovak
  "sl", // Slovenian
  "so", // Somali
  "es", // Spanish (generic)
  "es-ES", // Spanish (Spain)
  "es-MX", // Spanish (Mexico)
  "es-AR", // Spanish (Argentina)
  "sw", // Swahili
  "sv", // Swedish
  "tl", // Tagalog / Filipino
  "ta", // Tamil
  "te", // Telugu
  "th", // Thai
  "tr", // Turkish
  "uk", // Ukrainian
  "ur", // Urdu
  "uz", // Uzbek
  "vi", // Vietnamese
  "cy", // Welsh
  "xh", // Xhosa
  "yo", // Yoruba
  "zu", // Zulu
];
